import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IUser } from 'app/shared/models/users.model';
import { UsersService } from 'app/shared/services/users.service';
import { SelectFormFieldComponent } from '../forms/fields/select-form-field/select-form-field.component';
import { DatepickerFormFieldComponent } from '../forms/fields/datepicker-form-field/datepicker-form-field.component';
import { CaseFormSectionContainerComponent } from '../forms/case-form-section-container/case-form-section-container.component';
import { TextareaFormFieldComponent } from '../forms/fields/textarea-form-field/textarea-form-field.component';
import { RadioFormFieldComponent } from '../forms/fields/radio-form-field/radio-form-field.component';
import { QuestionsOptionsEnum } from 'app/shared/enums/questions-options.enum';
import { CaseFormService } from 'app/case-form/case-form.service';
import { take } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';
import { ISurveyTeam } from 'app/shared/models/case-closure-survey.model';
import { HelperService } from 'app/shared/services/helper.service';

@Component({
  selector: 'app-case-closure-survey',
  standalone: true,
  imports: [CommonModule, TranslateModule, ReactiveFormsModule, CaseFormSectionContainerComponent, SelectFormFieldComponent, DatepickerFormFieldComponent, TextareaFormFieldComponent, RadioFormFieldComponent],
  templateUrl: './case-closure-survey.component.html',
  styleUrl: './case-closure-survey.component.scss'
})
export class CaseClosureSurveyComponent {
  @Input() isOpen!: boolean;
  @Input() caseId!: number;
  @Input() surveyTeam!: ISurveyTeam;
  @Output() onClosePanel = new EventEmitter<boolean>();
  surveyForm: FormGroup | any;
  ownerUsers: IUser[] = [];
  internalCaseUsers: IUser[] = [];
  notifiedUsers: IUser[] = [];
  yesNoOptions = [
    { value: QuestionsOptionsEnum.YES, label: 'survey.labels.yes' },
    { value: QuestionsOptionsEnum.NO, label: 'survey.labels.no' }
  ];
  satisfiedOptions = [
    { value: QuestionsOptionsEnum.NOT_AT_ALL_SATISFIED, label: 'survey.labels.notAtAllSatisfied' },
    { value: QuestionsOptionsEnum.PARTIALLY_SATISFIED, label: 'survey.labels.partiallySatisfied' },
    { value: QuestionsOptionsEnum.MOSTLY_SATISFIED, label: 'survey.labels.mostlySatisfied' },
    { value: QuestionsOptionsEnum.SATISFIED, label: 'survey.labels.satisfied' },
    { value: QuestionsOptionsEnum.COMPLETELY_SATISFIED, label: 'survey.labels.completelySatisfied' }
  ];

  constructor(
    private usersService: UsersService,
    private fb: FormBuilder,
    private caseFormService: CaseFormService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private helperService: HelperService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && !changes['isOpen'].firstChange) {
      this.surveyForm.patchValue(this.surveyTeam);
      this.surveyForm.updateValueAndValidity();
    }
    if (this.surveyTeam) {
      this.getUsers();
    }
  }

  ngOnInit(): void {
    this.createForm();
  }

  createForm() {
    this.surveyForm = this.fb.group({});
    this.surveyForm.addControl('ticketOwnerId', new FormControl({ value: null, disabled: true }, [Validators.required]));
    this.surveyForm.addControl('internalCaseContactId', new FormControl({ value: null, disabled: true }, [Validators.required]));
    this.surveyForm.addControl('tsmNotifiedId', new FormControl({ value: null, disabled: true }, [Validators.required]));
    this.surveyForm.addControl('date', new FormControl({ value: new Date(), disabled: true }, [Validators.required]));

    this.surveyForm.addControl('q1SelectAnswer', new FormControl('', [Validators.required]));
    this.surveyForm.addControl('q2SelectAnswer', new FormControl('', [Validators.required]));
    this.surveyForm.addControl('q3SelectAnswer', new FormControl('', [Validators.required]));
    this.surveyForm.addControl('q4SelectAnswer', new FormControl('', [Validators.required]));
    this.surveyForm.addControl('q5SelectAnswer', new FormControl('', [Validators.required]));
    this.surveyForm.addControl('q1TextAnswer', new FormControl(null, []));
    this.surveyForm.addControl('q2TextAnswer', new FormControl(null, []));
    this.surveyForm.addControl('q3TextAnswer', new FormControl(null, []));
    this.surveyForm.addControl('q4TextAnswer', new FormControl(null, []));
    this.surveyForm.addControl('q5TextAnswer', new FormControl(null, []));
  }

  closePanel(submitted?: boolean) {
    this.onClosePanel.emit(submitted);
    this.isOpen = false;
  }

  getUsers() {
    this.usersService.users$.subscribe((users: IUser[]) => {
      this.ownerUsers = this.helperService.getUsersListWithSelectedUser(users, this.surveyTeam.ticketOwnerId as number, this.surveyTeam.ticketOwner);
      this.internalCaseUsers = this.helperService.getUsersListWithSelectedUser(users, this.surveyTeam.internalCaseContactId as number, this.surveyTeam.internalCaseContact);
      this.notifiedUsers = this.helperService.getUsersListWithSelectedUser(users, this.surveyTeam.tsmNotifiedId as number, this.surveyTeam.tsmNotified);
    });
  }

  submitSurvey() {
    this.caseFormService.submitClosureSurvey(this.caseId, this.surveyForm.getRawValue()).pipe(take(1)).subscribe(res => {
      this.toastr.show(this.translate.instant('messages.case_closure_survey_submitted'), ToastTypesEnum.SUCCESS);
      this.closePanel(true);
    });
  }

}
